// Copyright © 2023 Move Closer

import { AnyObject, Eventbus, IEventbus } from '@movecloser/front-core'
import { Component, Prop, Vue } from 'vue-property-decorator'

import {
  PlayerState,
  UiVideoPlayerPauseEventPayload,
  UiVideoPlayerPlayEventPayload,
  UiVideoPlayerStateChangedEventPayload
} from '../../../_contracts'

@Component<VideoPlayerOnNetwork>({
  name: 'VideoPlayerOnNetwork',

  mounted (): void {
    this.$nextTick(() => {
      if (this.shouldInit) {
        this.initPlayer()
      }
    })

    window.addEventListener('message', this.receiveMediaInfo)
    this.watchEvents()
  },

  beforeDestroy () {
    if (this.player && this.player.destroy) {
      this.player.destroy()
    }
    this.player = null
    this.clearEvents()
    window.removeEventListener('message', this.receiveMediaInfo)
  },

  template: `
    <div>
    <component :is="'script'" type="text/javascript" :src="videoMetaLink" async="true"/>
    <div :id="videoAttrId" :class="videoClass" ref="onVideo"/>
    </div>
  `
})
export class VideoPlayerOnNetwork extends Vue {
  @Prop({
    type: Object,
    required: true
  })
  public readonly baseVideoMetadata!: AnyObject

  @Prop({
    type: Object,
    required: false
  })
  public readonly eventBus?: IEventbus

  @Prop({
    type: String,
    required: true
  })
  public readonly videoAttrId!: string

  @Prop({
    type: String,
    required: false
  })
  public readonly videoAdsTag!: string

  @Prop({
    type: String,
    required: true
  })
  public readonly videoSrc!: string

  @Prop({
    type: String,
    required: false
  })
  public readonly videoClass?: string

  @Prop({
    type: String,
    required: true
  })
  public readonly origin!: string

  @Prop({
    type: Boolean,
    required: false,
    default: false
  })
  public readonly shouldInit!: boolean

  public isVideoPlaying: boolean = false

  private initEmited: boolean = false
  private player: AnyObject | null = null
  private eventHandlers: (ReturnType<Eventbus['handle']>)[] = []

  public get videoMetaLink (): string {
    return this.videoSrc + '&cId=' + this.videoAttrId + '&iid=' + this.videoAttrIdNumber
  }

  public onPlay (event: UiVideoPlayerPlayEventPayload): void {
    if (typeof event.payload === 'undefined') {
      return
    }

    const {
      playerId,
      autoplay,
      muted
    } = event.payload

    if (playerId !== this.videoAttrId) {
      return
    }
    this.initPlayer(autoplay, muted)

    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    if (window.ONTVsendCommand) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      window.ONTVsendCommand('play', this.videoAttrIdNumber, this.videoId)
    }
  }

  public onPause (event: UiVideoPlayerPauseEventPayload): void {
    if (typeof event.payload === 'undefined') {
      return
    }

    const { playerId } = event.payload

    if (playerId !== this.videoAttrId) {
      return
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    if (window.ONTVsendCommand) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      window.ONTVsendCommand('pause', this.videoAttrIdNumber, this.videoId)
    }
  }

  public onStateChanged (event: UiVideoPlayerStateChangedEventPayload): void {
    if (this.player === null || typeof event.payload === 'undefined') {
      return
    }

    const {
      playerId,
      state
    } = event.payload

    if (playerId === this.videoAttrId) {
      return
    }

    if (state === PlayerState.Playing) {
      this.player.pauseVideo()
    }
  }

  private watchEvents () {
    if (!this.eventBus) {
      return
    }

    this.eventHandlers = [
      this.eventBus.handle('ui:video-player.play', this.onPlay),
      this.eventBus.handle('ui:video-player.pause', this.onPause),
      this.eventBus.handle('ui:video-player.state-changed', this.onStateChanged)
    ]
  }

  private clearEvents () {
    this.eventHandlers.forEach(handler => handler.unsubscribe())
  }

  /**
   * Initialises the YouTube player.
   *
   * @param autoplay - Determines whether the video should start playing
   *   immediately after the player has been initialised.
   * @param muted - Determine whether the video should be started with volume 0.
   */
  private initPlayer (autoplay: boolean = false, muted: boolean = false): void {
    this.emitInit()

    this.player = null
  }

  private emitInit () {
    if (this.initEmited) {
      return
    }
    console.log('PlayerInit')
    this.$emit('init')
    this.initEmited = true
  }

  private get videoAttrIdNumber (): string | undefined {
    return this.videoAttrId.replace('uid-', '').replace('-video', '')
  }

  private get videoId (): string | undefined {
    // https://video.onnetwork.tv/embed.php?sid=M3JYLDAsMTBH
    const newLink = this.videoSrc

    const match = newLink.match(/sid=([^&]+)/)
    return match && match[1] ? match[1] : undefined
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  private receiveMediaInfo (e) {
    try {
      if (!e.data.indexOf || (e.data.indexOf('onntv://') !== 0)) {
        return
      }
      let com = { comm: '' }
      try {
        com = JSON.parse(e.data.substr(8))
      } catch (e) {
        com = { comm: '' }
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      if (com && com.comm === '') {
        return
      }
      switch (com.comm) {
        case '' :
          break
        case 'volumeset' :
          // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
          // @ts-ignore
          console.log(com.comm + ' ' + com.subject)
          break
        case 'playerLoaded' :
          // console.log('playerLoaded', true)
          break
        // eslint-disable-next-line no-fallthrough
        case 'adstarted':
          // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
          // @ts-ignore
          console.log(com.comm)
          this.emitInit()
          break
        case 'moviestarted':
          // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
          // @ts-ignore
          console.log(com.comm)
          this.emitInit()
          break
        case 'plstarttry' :
        case 'ad25':
        case 'ad50':
        case 'ad75':
        case 'adcomplete':
        case 'adskipped':
        case 'adnotpresent':
        case 'advideoexcluded':
        case 'adblocked':
        case 'aderror':
        case 'viewablesend':
        case 'movieset':
        case 'moviecleared' :
        case 'moviecompleted' :
        case 'movie25' :
        case 'movie50' :
        case 'movie75' :
        case 'adviewable' :
        case 'movieviewable' :
        case 'moviesetu' :
        case 'visibilitychange' :
        case 'moviesetdelayed' :
        case 'visibilitychangeum' :
        case 'movie95' :
        case 'moviepaused' :
        case 'movieresumed' :
          // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
          // @ts-ignore
          console.log(com.comm)
          break
      }
    } catch (e) {
    }
  }
}

export default VideoPlayerOnNetwork
